/**
 * Service Plans Page (Admin)
 * 관리자용 플랜 관리 페이지 (CRUD 지원)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  ServicePlan,
  ServicePlansRepository,
  PlanCreateRequest,
  PlanUpdateRequest,
  PlanLimits,
} from '../../repositories/ServicePlansRepository';

const servicePlansRepository = new ServicePlansRepository();

interface PlanFormData {
  name: string;
  display_name: string;
  price: number;
  billing_cycle: string;
  description: string;
  features: string[];
  limits: PlanLimits;
  is_active: boolean;
  sort_order: number;
  // 알림톡 관련 필드
  alimtalk_unit_price: number;
  alimtalk_free_quota: number;
}

const defaultFormData: PlanFormData = {
  name: '',
  display_name: '',
  price: 0,
  billing_cycle: 'monthly',
  description: '',
  features: [],
  limits: { max_students: 10, max_gates: 1, max_branches: 1 },
  is_active: true,
  sort_order: 0,
  // 알림톡 기본값
  alimtalk_unit_price: 13,
  alimtalk_free_quota: 0,
};

const ServicePlansPage: React.FC = () => {
  const [plans, setPlans] = useState<ServicePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<ServicePlan | null>(null);
  const [formData, setFormData] = useState<PlanFormData>(defaultFormData);
  const [featureInput, setFeatureInput] = useState('');
  const [saving, setSaving] = useState(false);

  // 플랜 목록 로드
  const loadPlans = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      if (!token) throw new Error('인증이 필요합니다. 다시 로그인해주세요.');

      const response = await servicePlansRepository.getAllPlans(token);
      if (response.success) {
        setPlans(response.plans);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '플랜 조회 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  // 모달 열기 (생성/수정)
  const openModal = (plan?: ServicePlan) => {
    if (plan) {
      setEditingPlan(plan);
      // 알림톡 무료 건수: 새 필드 우선, limits JSON 폴백
      const alimtalkFreeQuota = plan.alimtalk_free_quota ?? (plan.limits as any)?.alimtalk_monthly ?? 0;
      setFormData({
        name: plan.name,
        display_name: plan.display_name,
        price: plan.price,
        billing_cycle: plan.billing_cycle,
        description: plan.description || '',
        features: plan.features || [],
        limits: plan.limits || { max_students: 10, max_gates: 1, max_branches: 1 },
        is_active: Boolean(plan.is_active),
        sort_order: plan.sort_order || 0,
        alimtalk_unit_price: plan.alimtalk_unit_price ?? 13,
        alimtalk_free_quota: alimtalkFreeQuota,
      });
    } else {
      setEditingPlan(null);
      setFormData(defaultFormData);
    }
    setFeatureInput('');
    setIsModalOpen(true);
  };

  // 모달 닫기
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingPlan(null);
    setFormData(defaultFormData);
    setFeatureInput('');
  };

  // 기능 추가
  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        features: [...prev.features, featureInput.trim()],
      }));
      setFeatureInput('');
    }
  };

  // 기능 삭제
  const removeFeature = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  // 저장 (생성/수정)
  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('admin_token');
      if (!token) throw new Error('인증이 필요합니다. 다시 로그인해주세요.');

      if (editingPlan) {
        // 수정
        const updateData: PlanUpdateRequest = {
          display_name: formData.display_name,
          price: formData.price,
          description: formData.description,
          features: formData.features,
          limits: formData.limits,
          // 알림톡 관련 필드
          alimtalk_unit_price: formData.alimtalk_unit_price,
          alimtalk_free_quota: formData.alimtalk_free_quota,
        };
        await servicePlansRepository.updatePlan(token, editingPlan.id, updateData);
      } else {
        // 생성
        const createData: PlanCreateRequest = {
          name: formData.name,
          display_name: formData.display_name,
          price: formData.price,
          billing_cycle: formData.billing_cycle,
          description: formData.description,
          features: formData.features,
          limits: formData.limits,
          is_active: formData.is_active,
          sort_order: formData.sort_order,
          // 알림톡 관련 필드
          alimtalk_unit_price: formData.alimtalk_unit_price,
          alimtalk_free_quota: formData.alimtalk_free_quota,
        };
        await servicePlansRepository.createPlan(token, createData);
      }

      closeModal();
      loadPlans();
    } catch (err) {
      alert(err instanceof Error ? err.message : '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  // 삭제
  const handleDelete = async (plan: ServicePlan) => {
    if (!window.confirm(`"${plan.display_name}" 플랜을 삭제하시겠습니까?`)) return;

    try {
      const token = localStorage.getItem('admin_token');
      if (!token) throw new Error('인증이 필요합니다. 다시 로그인해주세요.');

      await servicePlansRepository.deletePlan(token, plan.id);
      loadPlans();
    } catch (err) {
      alert(err instanceof Error ? err.message : '삭제 실패');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">플랜 정보를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 className="text-red-800 font-medium mb-2">오류 발생</h3>
            <p className="text-red-600">{error}</p>
            <button
              onClick={loadPlans}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              다시 시도
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* 헤더 */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">서비스 플랜 관리</h1>
            <p className="text-gray-600">구독 플랜을 추가, 수정, 삭제할 수 있습니다.</p>
          </div>
          <button
            onClick={() => openModal()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
          >
            <span className="text-xl">+</span>
            플랜 추가
          </button>
        </div>

        {/* 플랜 목록 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className="bg-white rounded-lg shadow-md overflow-hidden border-2 border-gray-200 hover:border-indigo-500 transition-all"
            >
              {/* 플랜 헤더 */}
              <div
                className={`p-6 ${
                  plan.name === 'starter'
                    ? 'bg-gray-100'
                    : plan.name === 'basic'
                      ? 'bg-blue-50'
                      : plan.name === 'pro'
                        ? 'bg-indigo-100'
                        : plan.name === 'premium_v2' || plan.name === 'premium'
                          ? 'bg-purple-100'
                          : 'bg-gray-50'
                }`}
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.display_name}</h3>
                <div className="text-3xl font-bold text-gray-900">
                  {plan.price === 0 ? (
                    <span className="text-green-600">무료</span>
                  ) : (
                    <>
                      ₩{plan.price.toLocaleString()}
                      <span className="text-base font-normal text-gray-600">
                        /{plan.billing_cycle === 'annual' ? '년' : '월'}
                      </span>
                    </>
                  )}
                </div>
                {plan.billing_cycle === 'annual' && plan.price > 0 && (
                  <p className="text-sm text-green-600 mt-1">
                    월 ₩{Math.round(plan.price / 12).toLocaleString()} 상당
                  </p>
                )}
              </div>

              {/* 플랜 설명 */}
              <div className="p-6">
                <p className="text-sm text-gray-600 mb-4">{plan.description}</p>

                {/* 제한 사항 */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">📊 제한 사항</h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>
                      👥 학생:{' '}
                      {plan.limits.max_students >= 99999
                        ? '무제한'
                        : `최대 ${plan.limits.max_students}명`}
                    </li>
                    <li>
                      🚪 게이트:{' '}
                      {plan.limits.max_gates >= 99999
                        ? '무제한'
                        : `최대 ${plan.limits.max_gates}개`}
                    </li>
                    <li>
                      🏢 지점:{' '}
                      {plan.limits.max_branches >= 99999
                        ? '무제한'
                        : `최대 ${plan.limits.max_branches}개`}
                    </li>
                  </ul>
                </div>

                {/* 기능 목록 */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">✨ 기능</h4>
                  <ul className="space-y-1">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="text-sm text-gray-600 flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* 알림톡 설정 */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">📱 알림톡</h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>
                      🎁 무료 건수:{' '}
                      {(plan.alimtalk_free_quota ?? 0) >= 99999
                        ? '무제한'
                        : `${(plan.alimtalk_free_quota ?? 0).toLocaleString()}건/월`}
                    </li>
                    <li>
                      💳 단가: {plan.alimtalk_unit_price ?? 13}원/건
                    </li>
                  </ul>
                </div>
              </div>

              {/* 플랜 상태 및 액션 */}
              <div className="px-6 pb-6 flex justify-between items-center">
                <div
                  className={`text-xs px-3 py-1 rounded-full inline-block ${
                    plan.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {plan.is_active ? '활성화' : '비활성화'}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => openModal(plan)}
                    className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                  >
                    수정
                  </button>
                  <button
                    onClick={() => handleDelete(plan)}
                    className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                  >
                    삭제
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {plans.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <p className="text-gray-500 mb-4">등록된 플랜이 없습니다.</p>
            <button
              onClick={() => openModal()}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
            >
              첫 플랜 추가하기
            </button>
          </div>
        )}

        {/* 플랜 추가/수정 모달 */}
        {isModalOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => {
              setIsModalOpen(false);
              setEditingPlan(null);
            }}
          >
            <div
              className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold">
                  {editingPlan ? '플랜 수정' : '새 플랜 추가'}
                </h2>
              </div>

              <div className="p-6 space-y-4">
                {/* 플랜 식별자 (생성 시에만) */}
                {!editingPlan && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      플랜 식별자 (영문)
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="예: basic, pro, enterprise"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                )}

                {/* 표시명 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">표시명</label>
                  <input
                    type="text"
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    placeholder="예: 베이직, 프로, 엔터프라이즈"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* 가격 및 결제 주기 */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      가격 (원)
                    </label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      결제 주기
                    </label>
                    <select
                      value={formData.billing_cycle}
                      onChange={(e) => setFormData({ ...formData, billing_cycle: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="monthly">월간</option>
                      <option value="annual">연간</option>
                    </select>
                  </div>
                </div>

                {/* 설명 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">설명</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* 제한 사항 */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      최대 학생 수
                    </label>
                    <input
                      type="number"
                      value={formData.limits.max_students}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          limits: { ...formData.limits, max_students: Number(e.target.value) },
                        })
                      }
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">99999 = 무제한</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      최대 게이트 수
                    </label>
                    <input
                      type="number"
                      value={formData.limits.max_gates}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          limits: { ...formData.limits, max_gates: Number(e.target.value) },
                        })
                      }
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      최대 지점 수
                    </label>
                    <input
                      type="number"
                      value={formData.limits.max_branches}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          limits: { ...formData.limits, max_branches: Number(e.target.value) },
                        })
                      }
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                {/* 기능 목록 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">기능 목록</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={featureInput}
                      onChange={(e) => setFeatureInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                      placeholder="기능을 입력하고 Enter"
                      className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={addFeature}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      추가
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.features.map((feature, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm"
                      >
                        {feature}
                        <button
                          type="button"
                          onClick={() => removeFeature(index)}
                          className="text-indigo-500 hover:text-indigo-700"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* 알림톡 설정 */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">📱 알림톡 설정</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        무료 건수 (월)
                      </label>
                      <input
                        type="number"
                        value={formData.alimtalk_free_quota}
                        onChange={(e) =>
                          setFormData({ ...formData, alimtalk_free_quota: Number(e.target.value) })
                        }
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">99999 = 무제한</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        알림톡 단가 (원/건)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.alimtalk_unit_price}
                        onChange={(e) =>
                          setFormData({ ...formData, alimtalk_unit_price: Number(e.target.value) })
                        }
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>
                </div>

                {/* 정렬 순서 (생성 시에만) */}
                {!editingPlan && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      정렬 순서
                    </label>
                    <input
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) =>
                        setFormData({ ...formData, sort_order: Number(e.target.value) })
                      }
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                )}
              </div>

              <div className="p-6 border-t flex justify-end gap-3">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  취소
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saving ? '저장 중...' : editingPlan ? '수정' : '추가'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicePlansPage;
